import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, Pause, ArrowRight, Layers, Hash, Calendar, Quote, Share2, CheckCircle2, PlayCircle, Loader2, HelpCircle } from 'lucide-react';
import BrandCard from '../../components/features/BrandCard';
import { getPublicInsight, getPublicInsightQA } from '../../lib/apiClient';

export default function InsightPage() {
  const { insightSlug, topicSlug } = useParams();
  
  // Parse ID from slug
  const uuidMatch = insightSlug?.match(/^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/);
  const insightId = uuidMatch ? uuidMatch[1] : insightSlug;

  const [insight, setInsight] = useState(null);
  const [qaData, setQaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    async function loadInsight() {
        if (!insightId) return;
        try {
            setLoading(true);
            const [insightData, qaList] = await Promise.all([
              getPublicInsight(insightId),
              getPublicInsightQA(insightId).catch(() => [])
            ]);
            setInsight(insightData);
            setQaData(qaList || []);
        } catch (err) {
            console.error('Failed to load insight:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }
    loadInsight();
  }, [insightId]);

  if (loading) {
      return (
          <div className="min-h-screen bg-background flex items-center justify-center text-white">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
      );
  }

  if (error || !insight) {
      return (
          <div className="min-h-screen bg-background flex items-center justify-center text-white">
              <div className="text-center">
                  <h2 className="text-2xl font-bold mb-2">Insight not found</h2>
                  <Link to="/topics" className="text-primary hover:underline">Browse all topics</Link>
              </div>
          </div>
      );
  }

  // Transform API shape to UI shape if needed
  // API returns: { id, title, hook, body, takeaway, topics: {slug, title}, projects: {name, slug, logo_url} }
  // UI expects: topic object, brand object, content object etc.
  
  return (
    <div className="min-h-screen bg-background text-white font-sans pb-20">
      
      {/* Breadcrumb */}
      <div className="container mx-auto max-w-6xl px-6 pt-8 pb-4 text-sm text-slate-500">
        <Link to="/" className="hover:text-white transition-colors">Home</Link> &gt; 
        <Link to="/topics" className="hover:text-white transition-colors ml-1">Topics</Link> &gt; 
        {insight.topics && (
            <>
                <Link to={`/topics/${insight.topics.slug}`} className="hover:text-white transition-colors ml-1">{insight.topics.title}</Link> &gt; 
            </>
        )}
        <span className="text-white ml-1 line-clamp-1">Insight</span>
      </div>

      <div className="container mx-auto max-w-6xl px-6">
        <div className="grid lg:grid-cols-3 gap-12">
          
          {/* Main Content Column */}
          <div className="lg:col-span-2">
            
            {/* Insight Header */}
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight text-white">{insight.title}</h1>
              <p className="text-xl text-slate-300 leading-relaxed mb-6">{insight.hook}</p>
              
              <div className="flex flex-wrap gap-3 mb-6">
                {insight.topics && (
                    <Link to={`/topics/${insight.topics.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium hover:bg-primary/20 transition-colors">
                    <Hash className="w-3 h-3" /> {insight.topics.title}
                    </Link>
                )}
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface border border-white/10 text-slate-400 text-sm">
                  <Calendar className="w-3 h-3" /> {new Date(insight.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* Audio Player Placeholder */}
              <div className="bg-surface border border-white/10 rounded-xl p-4 flex items-center gap-4 shadow-lg">
                <button 
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center hover:scale-105 transition-transform shadow-glow-emerald"
                >
                  {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-1" />}
                </button>
                <div className="flex-1">
                  <div className="text-xs text-slate-400 mb-1">PLAYING SEGMENT</div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full w-1/3 bg-primary rounded-full" />
                  </div>
                </div>
                <div className="text-xs font-mono text-slate-400">00:00</div>
              </div>
            </div>

            {/* Structured Body */}
            <div className="space-y-10 text-slate-300 leading-relaxed">
              
              <section>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <span className="w-1 h-6 bg-primary rounded-full"/> The Insight
                </h3>
                <div className="prose prose-invert max-w-none">
                    {/* Render body text (assuming plain text or simple markdown for now) */}
                    <p className="whitespace-pre-wrap">{insight.body}</p>
                </div>
              </section>

              <section className="bg-surface/30 p-6 rounded-xl border border-white/5 relative">
                <Quote className="w-8 h-8 text-slate-600 absolute top-4 right-4 opacity-20" />
                <h3 className="text-sm font-bold text-slate-400 uppercase mb-2">The Takeaway</h3>
                <p className="italic text-slate-300 text-lg font-medium">{insight.takeaway}</p>
              </section>

            </div>

            {/* Action Bar - Creator CTA */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="p-8 rounded-2xl bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none" />
                 
                 <div>
                    <h3 className="text-xl font-bold text-white mb-2">Turn your episodes into insights like this.</h3>
                    <p className="text-slate-400 text-sm max-w-md">
                        Stop letting your best ideas disappear into the feed. Build your authority with PodVault.
                    </p>
                 </div>
                 
                 <Link to="/pricing" className="px-6 py-3 bg-white text-black font-bold rounded-full hover:bg-slate-200 transition-colors whitespace-nowrap flex items-center gap-2">
                    Explore Plans
                    <ArrowRight className="w-4 h-4" />
                 </Link>
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            
            {/* Q&A Section */}
            {qaData.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase mb-4 tracking-wider flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" />
                  Questions This Insight Answers
                </h3>
                <div className="space-y-3">
                  {qaData.map((qa) => (
                    <div key={qa.id} className="p-4 bg-surface border border-white/10 rounded-xl hover:border-primary/30 transition-colors">
                      <h4 className="font-semibold text-white text-sm mb-2">{qa.question}</h4>
                      <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">
                        {qa.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Brand Card */}
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase mb-4 tracking-wider">Insight By</h3>
              {insight.projects && (
                  <div className="p-6 bg-surface border border-white/10 rounded-2xl hover:border-primary/30 transition-colors">
                      <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-xl font-bold overflow-hidden">
                              {insight.projects.logo_url ? (
                                  <img src={insight.projects.logo_url} alt={insight.projects.name} className="w-full h-full object-cover"/>
                              ) : (
                                  insight.projects.name.charAt(0)
                              )}
                          </div>
                          <div>
                              <h4 className="font-bold text-white">{insight.projects.name}</h4>
                              <Link to={`/brands/${insight.projects.slug}`} className="text-xs text-primary hover:underline">View Profile</Link>
                          </div>
                      </div>
                  </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
